import { schemaResolver, useForm } from '@mantine/form';
import dayjs from 'dayjs';
import { filter, isNonNullish, mapToObj } from 'remeda';
import type {
  CompanyConfiguration,
  CustomVariable as CompanyCustomVariable,
} from 'shared/hooks/use-load-company-configuration';
import { CustomVariablesSchema } from 'shared/hooks/use-update-client-custom-variables';
import type { CustomVariable } from 'types/custom-variable-state';
import type { CustomVariableTypes } from 'types/custom-variable-types';
import type { Debtor } from 'types/store/client-state';

const convertDefaultValue = (defaultValue: string | null, varType: CustomVariableTypes) => {
  if (defaultValue === null || defaultValue === '') return null;

  switch (varType) {
    case 'string':
      return defaultValue;
    case 'number': {
      const numValue = Number(defaultValue);
      return Number.isNaN(numValue) ? null : numValue;
    }
    case 'boolean':
      return defaultValue === 'true';
    case 'date': {
      const parsedDate = dayjs(defaultValue);
      return parsedDate.isValid() ? parsedDate.toDate() : null;
    }
    default:
      return defaultValue;
  }
};

const createInitialValues = (
  variables: CompanyCustomVariable[],
  clientCustomVariables: CustomVariable,
) =>
  mapToObj(variables, variable => {
    const columnName = variable.column_name;
    // Implicit any here but we need to update the client type...
    const clientValue = clientCustomVariables[columnName as keyof typeof clientCustomVariables];

    if (isNonNullish(clientValue)) {
      return [columnName, convertDefaultValue(String(clientValue), variable.var_type)];
    }

    return [columnName, convertDefaultValue(variable.default_value, variable.var_type)];
  });

export const useCustomVariableForm = ({
  company,
  client,
}: {
  company: CompanyConfiguration;
  client: Debtor;
}) => {
  const availableCustomVariables = filter(
    company.custom_variables_attributes ?? [],
    variable => variable.model_type === 'debtor',
  );

  const customVariableValues = createInitialValues(
    availableCustomVariables,
    client.custom_variables,
  );

  const initialValues = { custom_variables: customVariableValues };

  const form = useForm({
    initialValues,
    validate: schemaResolver(CustomVariablesSchema, { sync: true }),
  });

  return { form, availableCustomVariables };
};
